import { ReactNode, useMemo } from 'react';
import { z } from 'zod';
import { FieldsContainer, OverlayedLoader } from '@mews-commander/ui';
import { createZodFormResolver, FormInputField, LocalizedText, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Size } from '@optimus-web/common';
import { Container, Stack, TextInputProps } from '@optimus-web/core';

interface TwoFactorFieldsProps {
    pinAutofocus?: boolean;
    inputSize?: Size;
    withPassword?: boolean;
}

export interface TwoFactorFormValues {
    pin: string;
    password?: string;
}

const pinValidationSchema = z
    .string()
    .min(6, { message: TextKey.ValidateValue })
    .max(8, { message: TextKey.ValidateValue })
    .min(1, { message: TextKey.ValidateAuthenticationCode });

const passwordValidationSchema = z.string().min(1, { message: TextKey.ValidatePassword });

export type TwoFactorFormProps = {
    id?: string;
    actions: { primary: ReactNode; secondary?: ReactNode };
    onSubmit: (values: TwoFactorFormValues) => void;
} & TwoFactorFieldsProps;

const defaultValues = {
    pin: '',
    password: '',
};

export function TwoFactorForm({ inputSize = 'large', onSubmit, actions, withPassword, id }: TwoFactorFormProps) {
    const { renderFormContent, isSubmitting } = useTwoFactorForm({ onSubmit, id });
    return renderFormContent(
        <TwoFactorFormContent
            inputSize={inputSize}
            withPassword={withPassword}
            actions={actions}
            isSubmitting={isSubmitting}
        />
    );
}

export function useTwoFactorForm({
    withPassword,
    onSubmit,
    id,
}: Pick<TwoFactorFormProps, 'withPassword' | 'id' | 'onSubmit'>) {
    const validationSchema = useMemo(
        () =>
            createZodFormResolver<TwoFactorFormValues>(
                z.object({
                    pin: pinValidationSchema,
                    password: withPassword ? passwordValidationSchema : z.string(),
                })
            ),
        [withPassword]
    );

    return useFormControls({
        onSubmit,
        id,
        resolver: validationSchema,
        defaultValues,
    });
}

function TwoFactorFormContent({
    inputSize,
    withPassword,
    actions,
    isSubmitting,
}: Omit<TwoFactorFormProps, 'onSubmit' | 'id'> & { isSubmitting: boolean }) {
    return (
        <Container position="relative">
            <Stack vertical spacing="300">
                <OverlayedLoader show={isSubmitting} />
                <FieldsContainer>
                    <PinInputField size={inputSize} autoFocus />
                    {withPassword && <PasswordInputField size={inputSize} />}
                </FieldsContainer>
                <Stack itemAlignment="end" distribution="space-between">
                    {actions.secondary}
                    {actions.primary}
                </Stack>
            </Stack>
        </Container>
    );
}

function PasswordInputField(props: Pick<TextInputProps, 'size' | 'autoFocus'>) {
    return (
        <FormInputField
            label={<LocalizedText textKey={TextKey.Password} />}
            name="password"
            type="password"
            required
            {...props}
        />
    );
}

export function PinInputField(props: Pick<TextInputProps, 'size' | 'autoFocus'>) {
    return (
        <FormInputField
            label={<LocalizedText textKey={TextKey.AuthenticationCode} />}
            name="pin"
            type="text"
            inputMode="numeric"
            data-lpignore={false}
            pattern="[0-9]*"
            required
            {...props}
        />
    );
}
